import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  ICreateFranchiseRequest,
  IFranchiseDataIncoming,
  IFranchiseTableData,
  IUpdateAccountFranchiseResponse
} from "@dealer-center/ddc-pages-franchise-management-interface";
import { flattenFranchiseData } from "./helper";

const queryKeyBase = "ddc-pages-franchise-management";

// Get all franchises
export function useFranchiseList(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [queryKeyBase, "list"],
    queryFn: () => getAllFranchises(),
    enabled: options?.enabled ?? true,
    retry: 1,
    staleTime: 1000 * 60 * 5 // 5 minutes
  });
}

// Get franchises for a specific account
export function useAccountFranchises(
  accountId: string,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: [queryKeyBase, "account", accountId],
    queryFn: () => getFranchisesForAccount(accountId),
    enabled: !!accountId && (options?.enabled ?? true),
    retry: 1,
    staleTime: 1000 * 60 * 5 // 5 minutes
  });
}

export function useDeleteFranchise() {
  const queryClient = useQueryClient();
  return useMutation({
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: [queryKeyBase, "list"] });
    },
    mutationFn: (id: string) => {
      return deleteFranchise(id);
    }
  });
}

export function useCreateFranchise(
  onSuccess?: (data: any) => void,
  onError?: (error: any) => void
) {
  const queryClient = useQueryClient();
  return useMutation({
    onSuccess: async data => {
      await queryClient.refetchQueries({
        queryKey: [queryKeyBase, "list"],
        type: "active"
      });
      if (onSuccess) {
        onSuccess(data);
      }
    },
    onError: error => {
      if (onError) {
        onError(error);
      }
    },
    mutationFn: (franchiseData: ICreateFranchiseRequest) => {
      return createFranchise(franchiseData);
    }
  });
}

// Add franchises to account
export function useAddFranchisesToAccount(
  onSuccess?: (data: IUpdateAccountFranchiseResponse) => void,
  onError?: (error: any) => void,
  skipInvalidation = false
) {
  const queryClient = useQueryClient();
  return useMutation({
    retry: 1,
    onSuccess: (data, variables, context) => {
      // Invalidate account franchises queries to refresh data
      if (!skipInvalidation) {
        queryClient.invalidateQueries({
          queryKey: [queryKeyBase, "account", variables.accountId]
        });
      }

      // Pass full response data to callback
      if (onSuccess) {
        onSuccess(data);
      }
    },
    onError: error => {
      if (onError) {
        onError(error);
      }
    },
    mutationFn: ({
      accountId,
      franchiseNames
    }: {
      accountId: string;
      franchiseNames: string[];
    }) => {
      return addFranchisesToAccount(accountId, franchiseNames);
    }
  });
}

// Remove franchises from account
export function useRemoveFranchisesFromAccount(
  onSuccess?: (data: IUpdateAccountFranchiseResponse) => void,
  onError?: (error: any) => void,
  skipInvalidation = false
) {
  const queryClient = useQueryClient();
  return useMutation({
    retry: 1,
    onSuccess: (data, variables, context) => {
      // Invalidate account franchises queries to refresh data
      if (!skipInvalidation) {
        queryClient.invalidateQueries({
          queryKey: [queryKeyBase, "account", variables.accountId]
        });
      }

      // Pass full response data to callback
      if (onSuccess) {
        onSuccess(data);
      }
    },
    onError: error => {
      if (onError) {
        onError(error);
      }
    },
    mutationFn: ({
      accountId,
      franchiseNames
    }: {
      accountId: string;
      franchiseNames: string[];
    }) => {
      return removeFranchisesFromAccount(accountId, franchiseNames);
    }
  });
}

const createFranchise = async (
  franchiseData: ICreateFranchiseRequest
): Promise<IFranchiseDataIncoming> => {
  try {
    const res = await fetch("/landing/nxlambda/franchiseManagement/franchise", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(franchiseData)
    });

    if (!res.ok) {
      throw new Error(
        `Failed to create franchise: ${res.status} ${res.statusText}`
      );
    }

    const data = await res.json();
    return data;
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(`Error creating franchise: ${error.message}`);
    }
    throw new Error("Unknown error occurred while creating franchise");
  }
};

const deleteFranchise = async (id: string): Promise<string> => {
  try {
    const res = await fetch(
      `/landing/nxlambda/franchiseManagement/franchise/${id}`,
      {
        method: "DELETE"
      }
    );

    if (!res.ok) {
      throw new Error(
        `Failed to delete franchise ${id}: ${res.status} ${res.statusText}`
      );
    }
    const data = await res.json();
    return data;
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(`Error deleting franchise ${id}: ${error.message}`);
    }
    throw new Error(`Unknown error occurred while deleting franchise ${id}`);
  }
};

const getAllFranchises = async (): Promise<IFranchiseTableData[]> => {
  try {
    const res = await fetch("/landing/nxlambda/franchiseManagement/franchise");
    if (!res.ok) {
      throw new Error(
        `Failed to fetch franchises: ${res.status} ${res.statusText}`
      );
    }

    const data = await res.json();
    const franchises = flattenFranchiseData(data);
    // Sort alphabetically by name
    return franchises.sort((a, b) => a.name.localeCompare(b.name));
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(`Error fetching franchise list: ${error.message}`);
    }
    throw new Error("Unknown error occurred while fetching franchise list");
  }
};

const getFranchisesForAccount = async (
  accountId: string
): Promise<IFranchiseDataIncoming[]> => {
  try {
    const res = await fetch(
      `/landing/nxlambda/franchiseManagement/accountFranchise/list/accountId/${accountId}`
    );

    if (!res.ok) {
      throw new Error(
        `Failed to fetch franchises for account ${accountId}: ${res.status} ${res.statusText}`
      );
    }

    const data = await res.json();
    return data;
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(
        `Error fetching franchises for account ${accountId}: ${error.message}`
      );
    }
    throw new Error(
      `Unknown error occurred while fetching franchises for account ${accountId}`
    );
  }
};

const addFranchisesToAccount = async (
  accountId: string,
  franchiseNames: string[]
): Promise<IUpdateAccountFranchiseResponse> => {
  try {
    const res = await fetch(
      `/landing/nxlambda/franchiseManagement/accountFranchise/add/accountId/${accountId}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ franchiseNames, accountId })
      }
    );

    if (!res.ok) {
      throw new Error(
        `Failed to add franchises to account ${accountId}: ${res.status} ${res.statusText}`
      );
    }

    const data = (await res.json()) as IUpdateAccountFranchiseResponse;
    return data;
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(
        `Error adding franchises to account ${accountId}: ${error.message}`
      );
    }
    throw new Error(
      `Unknown error occurred while adding franchises to account ${accountId}`
    );
  }
};

const removeFranchisesFromAccount = async (
  accountId: string,
  franchiseNames: string[]
): Promise<any> => {
  try {
    const res = await fetch(
      `/landing/nxlambda/franchiseManagement/accountFranchise/remove/accountId/${accountId}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ franchiseNames, accountId })
      }
    );

    if (!res.ok) {
      throw new Error(
        `Failed to remove franchises from account ${accountId}: ${res.status} ${res.statusText}`
      );
    }

    return await res.json();
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(
        `Error removing franchises from account ${accountId}: ${error.message}`
      );
    }
    throw new Error(
      `Unknown error occurred while removing franchises from account ${accountId}`
    );
  }
};
